// Type-ahead editor for fieldType "institution" (INSTITUTION-TYPEAHEAD-1, D-202.3).
//
// Modelled on AddressInput: one visible input, our own brand-styled dropdown, a 250 ms
// debounce and full keyboard navigation. The difference is where the matching happens —
// AddressInput asks Google on every keystroke, this one fetches the registry ONCE and
// matches in the browser. The list is a few dozen rows of global domain data, so a
// per-keystroke round trip would buy nothing and cost latency on every character.
//
// 🔴 FREE TEXT ALWAYS WINS (the s201 ruling, restated because it is the whole design).
// No suggestion is ever forced. Typing something the registry has never heard of and
// leaving is a COMPLETE, CORRECT value — it stores `{ display }` with no `ref`, renders
// as itself everywhere, and is never marked as an error or a warning. The registry
// improves what we can do for the executor when it matches; it takes nothing away when
// it does not. `"scotia bank mtg"` stays exactly that.
//
// Values (de)serialize through @shared/institution so the server consumers share the
// exact same shape, and a legacy plain string typed before the fieldType flip renders
// unchanged (G8).

import { useEffect, useMemo, useRef, useState, type KeyboardEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Input } from "@/components/ui/input";
import { CELL_BOX } from "@/components/interview/InterviewCells";
import { formatInstitutionSummary, toInstitutionValue } from "@shared/institution";

const MIN_QUERY_LEN = 1;
const MAX_SUGGESTIONS = 8;
const DEBOUNCE_MS = 250;

export type InstitutionOption = {
  key: string;
  name: string;
  aliases: string[] | null;
  kind: string;
};

/**
 * Case-insensitive prefix or word-start match on the name and every alias.
 *
 * Word-start rather than substring is deliberate: "roy" should offer "Royal Bank", but
 * "ion" should not offer every institution with "ion" buried mid-word. The matched
 * alias is carried back so the dropdown can say WHY a row matched — typing "BNS" and
 * being offered "Scotiabank" is confusing without it.
 */
export function matchInstitutions(
  query: string,
  options: readonly InstitutionOption[],
  limit = MAX_SUGGESTIONS,
): { option: InstitutionOption; via?: string }[] {
  const q = query.trim().toLowerCase();
  if (q.length < MIN_QUERY_LEN) return [];
  const startsWord = (haystack: string): boolean => {
    const h = haystack.toLowerCase();
    if (h.startsWith(q)) return true;
    // A word start anywhere in the string: preceded by a space or a separator.
    return new RegExp(`[\\s\\-/&.,(]${q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}`).test(h);
  };
  const out: { option: InstitutionOption; via?: string }[] = [];
  for (const option of options) {
    if (startsWord(option.name)) {
      out.push({ option });
      continue;
    }
    const alias = (option.aliases ?? []).find(startsWord);
    if (alias) out.push({ option, via: alias });
  }
  // Exact-prefix-on-name first, then everything else, both alphabetical within their group.
  return out
    .sort((a, b) => {
      const aPre = a.option.name.toLowerCase().startsWith(q) ? 0 : 1;
      const bPre = b.option.name.toLowerCase().startsWith(q) ? 0 : 1;
      return aPre !== bPre ? aPre - bPre : a.option.name.localeCompare(b.option.name);
    })
    .slice(0, limit);
}

export function InstitutionInput({ value, onChange, onBlur, readOnly, fieldId, placeholder, interviewLook = false }: {
  value: string;
  onChange: (v: string) => void;
  onBlur: () => void;
  readOnly?: boolean;
  fieldId: string;
  placeholder?: string;
  /** s235 (D-235.34; findings item 21, Phase 0 section B) — the interview's box: CELL_BOX (#FEFCF8, 44px, 8px radius),
   *  drawn as the typed boxes beside it (`IconTextCell`), not the dashboard's `<Input>` (#FAF7F1). Default OFF: the
   *  dashboard pages keep their look. */
  interviewLook?: boolean;
}) {
  // What the planner sees and edits: the display half of the stored value, or the raw
  // string when the value is a legacy plain string (G8 — it is shown, never discarded).
  const display = useMemo(() => formatInstitutionSummary(value), [value]);

  // One fetch for the whole session. `staleTime: Infinity` because the registry changes
  // on deploy, not while a planner is typing. A failed fetch degrades to a plain input
  // with no dropdown and no error surfaced — the same graceful shape AddressInput uses
  // when Places is unavailable.
  const { data: options } = useQuery<InstitutionOption[]>({
    queryKey: ["/api/institutions"],
    staleTime: Infinity,
    gcTime: Infinity,
    retry: false,
    enabled: !readOnly,
  });

  const [suggestions, setSuggestions] = useState<{ option: InstitutionOption; via?: string }[]>([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const blurTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onBlurRef = useRef(onBlur);
  onBlurRef.current = onBlur;

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (blurTimerRef.current) clearTimeout(blurTimerRef.current);
    };
  }, []);

  const handleChange = (typed: string) => {
    // Typing REPLACES any previous match: the ref described the old name, so carrying it
    // forward would leave a value whose pointer and text disagree. It is re-earned only by
    // picking a suggestion.
    onChange(toInstitutionValue(typed));
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const list = matchInstitutions(typed, options ?? []);
      setSuggestions(list);
      setActiveIndex(-1);
      setOpen(list.length > 0);
    }, DEBOUNCE_MS);
  };

  const selectSuggestion = (s: { option: InstitutionOption }) => {
    setOpen(false);
    setSuggestions([]);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    onChange(toInstitutionValue(s.option.name, s.option.key));
    // Defer the save one tick, for the same reason AddressInput does: onChange writes into
    // the parent's local-edit state asynchronously and the parent's blur handler READS that
    // state to persist it. Calling blur synchronously would read the pre-change value.
    setTimeout(() => onBlurRef.current(), 0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIndex((i) => Math.min(i + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      // Enter with nothing highlighted commits what the planner TYPED. It never
      // auto-selects the top suggestion — that is how free typing gets silently
      // overwritten by a near-match.
      if (activeIndex >= 0) {
        e.preventDefault();
        selectSuggestion(suggestions[activeIndex]);
      }
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const handleBlur = () => {
    onBlur(); // field autosave — the typed value stands, matched or not
    blurTimerRef.current = setTimeout(() => setOpen(false), 150);
  };

  if (readOnly) {
    return display
      ? <span className="text-sm">{display}</span>
      : <span className="text-sm text-muted-foreground">—</span>;
  }

  return (
    <div className="relative" data-testid={`input-institution-${fieldId}`}>
      {interviewLook ? (
        <input
          value={display}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={placeholder ?? "Start typing…"}
          aria-label={placeholder}
          className={`${CELL_BOX} pl-3 pr-3`}
          autoComplete="off"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          data-look="interview"
        />
      ) : (
      <Input
        value={display}
        onChange={(e) => handleChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        placeholder={placeholder ?? "Start typing…"}
        className="text-sm"
        autoComplete="off"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
      />
      )}
      {open && suggestions.length > 0 && (
        <ul className="address-suggestions" role="listbox" data-testid={`institution-suggestions-${fieldId}`}>
          {suggestions.map((s, i) => (
            <li
              key={s.option.key}
              role="option"
              aria-selected={i === activeIndex}
              className={`address-suggestion-item${i === activeIndex ? " is-active" : ""}`}
              // mousedown (not click) + preventDefault keeps focus on the input so the
              // blur-driven close doesn't fire before the selection lands.
              onMouseDown={(e) => {
                e.preventDefault();
                selectSuggestion(s);
              }}
              onMouseEnter={() => setActiveIndex(i)}
            >
              {s.option.name}
              {s.via ? <span className="text-muted-foreground"> · {s.via}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
